import { useState, useEffect, useCallback } from 'react';

interface NetworkRequest {
  id: string;
  url: string;
  method: string;
  startTime: number;
  status: 'pending' | 'completed' | 'failed';
  type: 'xhr' | 'fetch' | 'other';
}

export const useNetworkMonitor = () => {
  const [activeRequests, setActiveRequests] = useState<Map<string, NetworkRequest>>(new Map());
  const [requestCount, setRequestCount] = useState(0);

  // Monitor XMLHttpRequest
  useEffect(() => {
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    const xhrRequests = new Map<string, NetworkRequest>();

    XMLHttpRequest.prototype.open = function(method: string, url: string) {
      const requestId = `xhr-${Date.now()}-${Math.random()}`;
      const request: NetworkRequest = {
        id: requestId,
        url: url.toString(),
        method: method.toUpperCase(),
        startTime: Date.now(),
        status: 'pending',
        type: 'xhr'
      };

      xhrRequests.set(requestId, request);
      setActiveRequests(prev => new Map(prev).set(requestId, request));
      setRequestCount(prev => prev + 1);

      // Store the request ID on the XHR instance
      (this as any).requestId = requestId;

      return originalXHROpen.apply(this, arguments as any);
    };

    XMLHttpRequest.prototype.send = function() {
      const requestId = (this as any).requestId;
      if (requestId) {
        const xhr = this;
        
        xhr.addEventListener('load', () => {
          const request = xhrRequests.get(requestId);
          if (request) {
            request.status = 'completed';
            xhrRequests.delete(requestId);
            setActiveRequests(prev => {
              const newMap = new Map(prev);
              newMap.delete(requestId);
              return newMap;
            });
          }
        });

        xhr.addEventListener('error', () => {
          const request = xhrRequests.get(requestId);
          if (request) {
            request.status = 'failed';
            xhrRequests.delete(requestId);
            setActiveRequests(prev => {
              const newMap = new Map(prev);
              newMap.delete(requestId);
              return newMap;
            });
          }
        });
      }

      return originalXHRSend.apply(this, arguments as any);
    };

    return () => {
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
    };
  }, []);

  // Monitor fetch requests
  useEffect(() => {
    const originalFetch = window.fetch;
    const fetchRequests = new Map<string, NetworkRequest>();

    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      const url = typeof input === 'string' ? input : input.toString();
      const method = init?.method || 'GET';
      const requestId = `fetch-${Date.now()}-${Math.random()}`;
      
      const request: NetworkRequest = {
        id: requestId,
        url,
        method: method.toUpperCase(),
        startTime: Date.now(),
        status: 'pending',
        type: 'fetch'
      };

      fetchRequests.set(requestId, request);
      setActiveRequests(prev => new Map(prev).set(requestId, request));
      setRequestCount(prev => prev + 1);

      return originalFetch(input, init).then(
        (response) => {
          const request = fetchRequests.get(requestId);
          if (request) {
            request.status = 'completed';
            fetchRequests.delete(requestId);
            setActiveRequests(prev => {
              const newMap = new Map(prev);
              newMap.delete(requestId);
              return newMap;
            });
          }
          return response;
        },
        (error) => {
          const request = fetchRequests.get(requestId);
          if (request) {
            request.status = 'failed';
            fetchRequests.delete(requestId);
            setActiveRequests(prev => {
              const newMap = new Map(prev);
              newMap.delete(requestId);
              return newMap;
            });
          }
          throw error;
        }
      );
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const getActiveRequestCount = useCallback(() => {
    return activeRequests.size;
  }, [activeRequests]);

  const getActiveRequests = useCallback(() => {
    return Array.from(activeRequests.values());
  }, [activeRequests]);

  const waitForAllRequests = useCallback(async (maxWaitTime: number = 60000): Promise<boolean> => {
    const startTime = Date.now();
    
    while (activeRequests.size > 0 && (Date.now() - startTime) < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (activeRequests.size > 0) {
      return false;
    }
    
    return true;
  }, [activeRequests]);

  return {
    activeRequestCount: getActiveRequestCount(),
    activeRequests: getActiveRequests(),
    totalRequestCount: requestCount,
    waitForAllRequests,
    getActiveRequestCount,
    getActiveRequests
  };
};
